/**
 * Image preprocessing pipeline before sending to OCR/AI.
 * Both functions accept a full data URL and return processed base64 (no prefix).
 */

const MAX_DIM = 1500;
const MIN_DIM = 1024;

async function loadBitmapWithOrientation(dataUrl) {
  const res = await fetch(dataUrl);
  const blob = await res.blob();
  return createImageBitmap(blob, { imageOrientation: 'from-image' });
}

function makeCanvas(bitmap) {
  const longSide = Math.max(bitmap.width, bitmap.height);
  const scale =
    longSide < MIN_DIM
      ? Math.min(MIN_DIM / longSide, 2)
      : longSide > MAX_DIM
        ? MAX_DIM / longSide
        : 1;
  const w = Math.round(bitmap.width * scale);
  const h = Math.round(bitmap.height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(bitmap, 0, 0, w, h);
  return { canvas, ctx, w, h };
}

function contrastStretch(d) {
  let lo = 255, hi = 0;
  for (let i = 0; i < d.length; i += 4) {
    if (d[i] < lo) lo = d[i];
    if (d[i] > hi) hi = d[i];
  }
  const span = hi - lo || 1;
  for (let i = 0; i < d.length; i += 4) {
    const v = Math.min(255, Math.round(((d[i] - lo) / span) * 255));
    d[i] = d[i + 1] = d[i + 2] = v;
  }
}

function sharpen(ctx, w, h) {
  const before = ctx.getImageData(0, 0, w, h);
  const src = new Uint8ClampedArray(before.data);
  const out = ctx.getImageData(0, 0, w, h);
  const dst = out.data;
  const K = [0, -1, 0, -1, 5, -1, 0, -1, 0];
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let rAcc = 0, gAcc = 0, bAcc = 0;
      for (let ky = -1; ky <= 1; ky++) {
        for (let kx = -1; kx <= 1; kx++) {
          const px = Math.min(w - 1, Math.max(0, x + kx));
          const py = Math.min(h - 1, Math.max(0, y + ky));
          const kidx = (ky + 1) * 3 + (kx + 1);
          const sidx = (py * w + px) * 4;
          rAcc += src[sidx]     * K[kidx];
          gAcc += src[sidx + 1] * K[kidx];
          bAcc += src[sidx + 2] * K[kidx];
        }
      }
      const idx = (y * w + x) * 4;
      dst[idx]     = Math.min(255, Math.max(0, rAcc));
      dst[idx + 1] = Math.min(255, Math.max(0, gAcc));
      dst[idx + 2] = Math.min(255, Math.max(0, bAcc));
    }
  }
  ctx.putImageData(out, 0, 0);
}

/**
 * Pipeline for ODOMETER images:
 * EXIF-orient → scale → grayscale → contrast stretch → sharpen
 * Returns processed base64 string (no data: prefix).
 */
export async function preprocessOdometerImage(dataUrl) {
  const bitmap = await loadBitmapWithOrientation(dataUrl);
  const { canvas, ctx, w, h } = makeCanvas(bitmap);
  bitmap.close?.();

  const raw = ctx.getImageData(0, 0, w, h);
  const d = raw.data;

  // Grayscale (luminosity)
  for (let i = 0; i < d.length; i += 4) {
    const g = Math.round(0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2]);
    d[i] = d[i + 1] = d[i + 2] = g;
  }

  contrastStretch(d);
  ctx.putImageData(raw, 0, 0);
  sharpen(ctx, w, h);

  return canvas.toDataURL('image/jpeg', 0.95).split(',')[1];
}

function contrastStretchRobust(d) {
  const vals = [];
  for (let i = 0; i < d.length; i += 4) vals.push(d[i]);
  vals.sort((a, b) => a - b);
  const lo = vals[Math.floor(vals.length * 0.02)];
  const hi = vals[Math.floor(vals.length * 0.98)];
  const span = hi - lo || 1;
  for (let i = 0; i < d.length; i += 4) {
    const v = Math.min(255, Math.max(0, Math.round(((d[i] - lo) / span) * 255)));
    d[i] = d[i + 1] = d[i + 2] = v;
  }
}

function adaptiveThreshold(ctx, w, h, blockSize = 15, C = 10) {
  const img = ctx.getImageData(0, 0, w, h);
  const d = img.data;
  const out = new Uint8ClampedArray(d);
  const half = Math.floor(blockSize / 2);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let sum = 0;
      let count = 0;
      for (let ky = -half; ky <= half; ky++) {
        for (let kx = -half; kx <= half; kx++) {
          const px = Math.min(w - 1, Math.max(0, x + kx));
          const py = Math.min(h - 1, Math.max(0, y + ky));
          sum += d[(py * w + px) * 4];
          count++;
        }
      }
      const mean = sum / count;
      const idx = (y * w + x) * 4;
      const v = d[idx] < mean - C ? 0 : 255;
      out[idx] = v;
      out[idx + 1] = v;
      out[idx + 2] = v;
      out[idx + 3] = 255;
    }
  }
  const result = ctx.getImageData(0, 0, w, h);
  result.data.set(out);
  ctx.putImageData(result, 0, 0);
}

/**
 * Pipeline for RECEIPT images (fuel bills):
 * EXIF-orient → scale → grayscale → robust contrast → adaptive threshold → sharpen
 * Returns processed base64 string (no data: prefix).
 */
export async function preprocessReceiptImage(dataUrl) {
  const bitmap = await loadBitmapWithOrientation(dataUrl);
  const { canvas, ctx, w, h } = makeCanvas(bitmap);
  bitmap.close?.();

  const raw = ctx.getImageData(0, 0, w, h);
  const d = raw.data;

  // 1. Grayscale
  for (let i = 0; i < d.length; i += 4) {
    const g = Math.round(0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2]);
    d[i] = d[i + 1] = d[i + 2] = g;
  }

  // 2. Robust contrast stretch (percentile-based, ป้องกัน outlier)
  contrastStretchRobust(d);
  ctx.putImageData(raw, 0, 0);

  // 3. Adaptive threshold (แก้แสงไม่สม่ำเสมอในรูปเดียวกัน)
  adaptiveThreshold(ctx, w, h, 31, 10);

  // 4. Sharpen (ทำสุดท้าย ให้ขอบตัวอักษรคมขึ้นหลัง threshold)
  sharpen(ctx, w, h);

  return canvas.toDataURL('image/jpeg', 0.95).split(',')[1];
}

/**
 * Pipeline for DOCUMENT images (receipts, ID cards):
 * EXIF-orient → scale → per-channel contrast stretch → sharpen  — NO grayscale
 * Returns processed base64 string (no data: prefix).
 */
export async function preprocessDocumentImage(dataUrl) {
  const bitmap = await loadBitmapWithOrientation(dataUrl);
  const { canvas, ctx, w, h } = makeCanvas(bitmap);
  bitmap.close?.();

  const raw = ctx.getImageData(0, 0, w, h);
  const d = raw.data;

  // Contrast stretch per channel independently to preserve color balance
  for (let ch = 0; ch < 3; ch++) {
    let lo = 255;
    let hi = 0;
    for (let i = ch; i < d.length; i += 4) {
      if (d[i] < lo) lo = d[i];
      if (d[i] > hi) hi = d[i];
    }
    const span = hi - lo || 1;
    for (let i = ch; i < d.length; i += 4) {
      d[i] = Math.min(255, Math.round(((d[i] - lo) / span) * 255));
    }
  }

  ctx.putImageData(raw, 0, 0);
  sharpen(ctx, w, h);

  return canvas.toDataURL('image/jpeg', 0.95).split(',')[1];
}
